export type DateFilterConfig = { value?: string; mode?: string; timezone?: string; window?: "previous-evening" | "day-start" };
export const todayGmt7 = (): string => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};
const addDays = (day: string, amount: number): string => {
  const value = new Date(`${day}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + amount);
  return value.toISOString().slice(0, 10);
};
export const validFutureDate = (day: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day <= todayGmt7()) return false;
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day;
};
export const selectedMatchWindow = (day: string) => ({
  fromDate: `${addDays(day, -1)}T20:00:00+07:00`,
  toDate: `${addDays(day, 1)}T12:00:00+07:00`,
});
const label = (day: string) => day.split('-').reverse().join('/');

/** One root-scoped controller; templates choose presentation with an empty slot. */
export const mountDateFilter = (root: HTMLElement, config: DateFilterConfig, onChange: (date: string) => void, onSelect?: () => void) => {
  let selected = config.value && validFutureDate(config.value) ? config.value : '';
  let slots = Array.from(root.querySelectorAll<HTMLElement>('[data-gwd-date-filter]'));
  if (!slots.length) {
    const slot = document.createElement('div');
    slot.dataset.gwdDateFilter = config.mode || 'tabs';
    root.prepend(slot);
    slots = [slot];
  }
  const syncers: (() => void)[] = [];
  const choose = (value: string) => {
    if (value && !validFutureDate(value)) return;
    onSelect?.();
    if (selected === value) return;
    selected = value;
    syncers.forEach((sync) => sync());
    onChange(value);
  };
  slots.forEach((slot) => {
    slot.classList.add('gwd-date-filter');
    const mode = slot.dataset.gwdDateFilter || config.mode || 'tabs';
    if (mode === 'navigation') {
      slot.classList.add('gwd-date-filter--navigation');
      const previous = document.createElement('button'); previous.type = 'button'; previous.textContent = '‹'; previous.setAttribute('aria-label', 'Ngày trước');
      const next = document.createElement('button'); next.type = 'button'; next.textContent = '›'; next.setAttribute('aria-label', 'Ngày tiếp theo');
      const picker = document.createElement('label'); picker.className = 'gwd-date-filter__calendar';
      const display = document.createElement('span'); display.className = 'gwd-date-filter__date-text';
      const input = document.createElement('input'); input.type = 'date'; input.setAttribute('aria-label', 'Ngày thi đấu (GMT+7)');
      picker.append(display, input); slot.append(previous, picker, next);
      const selectDay = (day: string) => choose(day === todayGmt7() ? '' : day);
      previous.addEventListener('click', () => selectDay(addDays(selected || todayGmt7(), -1)));
      next.addEventListener('click', () => selectDay(addDays(selected || todayGmt7(), 1)));
      input.addEventListener('change', () => {
        if (!input.value) { sync(); return; }
        if (input.value !== todayGmt7() && !validFutureDate(input.value)) { sync(); return; }
        selectDay(input.value);
      });
      const sync = () => {
        const today = todayGmt7(); const day = selected || today;
        input.min = today; input.value = day; display.textContent = day;
        previous.disabled = day <= today;
      };
      slot.addEventListener('focusin', () => { input.min = todayGmt7(); });
      syncers.push(sync); sync();
      return;
    }
    const dateLabel = document.createElement('label');
    dateLabel.className = 'gwd-date-filter__picker';
    const caption = document.createElement('span'); caption.textContent = 'Chọn ngày';
    const input = document.createElement('input'); input.type = 'date'; input.setAttribute('aria-label', 'Chọn ngày tương lai');
    dateLabel.append(caption, input);
    const status = document.createElement('span'); status.className = 'gwd-date-filter__status'; status.setAttribute('aria-live', 'polite');
    const buttons: { node: HTMLButtonElement; value: () => string }[] = [];
    const button = (text: string, value: () => string) => {
      const node = document.createElement('button'); node.type = 'button'; node.textContent = text;
      node.addEventListener('click', () => choose(value())); slot.append(node); buttons.push({ node, value });
    };
    let select: HTMLSelectElement | undefined;
    if (mode === 'select') {
      select = document.createElement('select'); select.setAttribute('aria-label', 'Ngày thi đấu');
      slot.append(select);
      select.addEventListener('change', () => {
        if (select!.value === 'custom') { dateLabel.hidden = false; input.focus(); return; }
        choose(select!.value);
      });
    } else {
      button('Hiện tại', () => '');
      if (mode !== 'date') button('Ngày mai', () => addDays(todayGmt7(), 1));
    }
    slot.append(dateLabel, status);
    const sync = () => {
      input.min = addDays(todayGmt7(), 1); input.value = selected;
      input.setCustomValidity('');
      buttons.forEach(({ node, value }) => { node.setAttribute('aria-pressed', String(value() === selected)); });
      if (select) {
        const values = [{ value: '', text: 'Hiện tại' }, ...Array.from({ length: 7 }, (_, index) => {
          const day = addDays(todayGmt7(), index + 1); return { value: day, text: index === 0 ? 'Ngày mai' : label(day) };
        })];
        if (selected && !values.some((item) => item.value === selected)) values.push({ value: selected, text: label(selected) });
        values.push({ value: 'custom', text: 'Chọn ngày khác…' });
        select.replaceChildren(...values.map((item) => { const option = document.createElement('option'); option.value = item.value; option.textContent = item.text; return option; }));
        select.value = selected; dateLabel.hidden = true;
      }
      status.textContent = selected ? `${config.window === "day-start" ? `00h ${label(selected)}` : `20h ${label(addDays(selected, -1))}`} – 12h ${label(addDays(selected, 1))} (GMT+7)` : '';
    };
    input.addEventListener('change', () => {
      if (input.value && !validFutureDate(input.value)) {
        input.setCustomValidity('Chỉ được chọn ngày sau hôm nay theo GMT+7.'); input.reportValidity(); return;
      }
      choose(input.value);
    });
    slot.addEventListener('focusin', () => { input.min = addDays(todayGmt7(), 1); });
    syncers.push(sync); sync();
  });
  // Re-evaluate the minimum and selected scope after midnight or a restored tab.
  const reconcileDay = () => {
    if (selected && !validFutureDate(selected)) choose('');
    else syncers.forEach((sync) => sync());
  };
  window.addEventListener('focus', reconcileDay);
  window.addEventListener('pageshow', reconcileDay);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) reconcileDay(); });
  return { select: choose };
};
